import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import ImageService from '../api/service/image/ImageService';

// ✅ 스타일 컴포넌트 정의
const ImageContainer = styled.div<{ $small: boolean }>`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  margin: 0 auto;
  max-width: ${(props) =>
    props.$small ? '200px' : '700px'}; /* small에 따라 크기 조절 */
  aspect-ratio: 16 / 9; /* 16:9 비율 유지 */
  overflow: hidden; /* 부모 영역을 넘어가는 부분 숨김 */
  background-color: #f0f0f0; /* 이미지가 로드되지 않을 경우를 대비한 배경 */
`;

const UploadedImage = styled.img`
  width: 100%; /* 이미지가 부모 컨테이너에 맞춰 전체 너비를 채움 */
  height: 100%; /* 이미지가 부모 컨테이너에 맞춰 전체 높이를 채움 */
  object-fit: contain; /* 이미지 비율을 유지하며 전체가 보이도록 조정 */
  position: absolute; /* 컨테이너 안에서 정확히 배치 */
  top: 0;
  left: 0;
`;

interface BaseImageResponse {
  originalUrl: string;
  originalWidth: number;
  originalHeight: number;
  resizedUrl: string;
  resizedWidth: number;
  resizedHeight: number;
}

interface ImagePreviewProps {
  onClick?: () => void; // ✅ onClick 속성 추가
  image: BaseImageResponse;
  alt: string;
  small?: boolean;
}

// ✅ encryptedId 추출 함수
const extractEncryptedId = (url: string): string => {
  const parts = url.split('/'); // 슬래시로 분리
  const indexOfImages = parts.indexOf('images'); // "images"의 위치를 찾음
  if (indexOfImages === -1) {
    throw new Error('URL에서 "images" 경로를 찾을 수 없습니다.');
  }
  return parts.slice(indexOfImages + 1).join('/'); // "images" 이후의 모든 경로를 조합
};

// ✅ ImagePreview 컴포넌트 정의
const ImagePreview: React.FC<ImagePreviewProps> = ({
  image,
  alt,
  onClick,
  small = false,
}) => {
  const [isOriginalLoaded, setIsOriginalLoaded] = useState<boolean>(false); // ✅ 원본 이미지 로드 상태 관리
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const [resizedImageUrl, setResizedImageUrl] = useState<string | null>(null);
  const [originalImageUrl, setOriginalImageUrl] = useState<string | null>(null);

  useEffect(() => {
    // 이미지 리소스 조회
    const fetchImages = async () => {
      try {
        // Extract encryptedId from resizedUrl and originalUrl
        const resizedId = extractEncryptedId(image.resizedUrl);
        const originalId = extractEncryptedId(image.originalUrl);

        // Fetch URLs from ImageService
        const resizedUrl = await ImageService.getImageResource(`${resizedId}`);
        const originalUrl = await ImageService.getImageResource(
          `${originalId}`
        );

        setResizedImageUrl(resizedUrl);
        setOriginalImageUrl(originalUrl);
      } catch (error) {
        console.error('이미지 조회 실패:', error);
      }
    };

    fetchImages();
  }, [image]);

  // 이미지 로드 완료시 컨테이너 높이 조정
  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setContainerHeight(img.offsetHeight);
    setIsOriginalLoaded(true);
  };

  return (
    <ImageContainer
      onClick={onClick}
      $small={small}
      style={{
        paddingBottom: containerHeight ? 0 : '56.25%',
      }}
    >
      {resizedImageUrl && (
        <UploadedImage
          src={resizedImageUrl} // ✅ resizedUrl 먼저 보여줌
          alt={alt}
          style={{ opacity: isOriginalLoaded ? 0 : 1 }} // ✅ original이 로드되기 전만 보임
        />
      )}
      {originalImageUrl && (
        <UploadedImage
          src={originalImageUrl} // ✅ originalUrl을 나중에 보여줌
          alt={alt}
          onLoad={handleImageLoad} // ✅ originalUrl이 로드되면 상태 변경
          style={{ opacity: isOriginalLoaded ? 1 : 0 }}
        />
      )}
    </ImageContainer>
  );
};

export default ImagePreview;
